import React from 'react';

import { ThemeProvider } from '@mui/material/styles';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { ReactQueryDevtools } from '@tanstack/react-query-devtools';
import { SnackbarProvider } from 'notistack';
import { CookiesProvider } from 'react-cookie';
import { Provider as ReduxStoreProvider } from 'react-redux';
import { RouterProvider } from 'react-router';
import { CssBaseline, GlobalStyles } from '@/shared/ui/providers/CssGlobals';

import { darkModeTheme } from '@/config/mui';
import { Z_INDEX } from '@/config/z-index';
import ResponseMessage from '@/features/_shared/ResponseMessage';
import { useImpostorRefresh } from '@/features/auth-access';
import { AddToAppContextProvider } from '@/features/dashboard/state/AppContext';
import { GlobalLoaderProvider } from '@/providers/GlobalLoaderProvider';
import { useFeatureFlagRefresh } from '@/services/feature-flags/useFeatureFlagRefresh';
import { SnackbarNotificationsProvider } from '@/shared/hooks/snackbarNotifications.context';

import { store } from '@/store';
import { router } from './routes';

const queryClient = new QueryClient();

const App: React.FC = () => {
  useImpostorRefresh();
  useFeatureFlagRefresh();
  return (
    <CookiesProvider>
      <QueryClientProvider client={queryClient}>
        <ThemeProvider theme={darkModeTheme}>
          <ReduxStoreProvider store={store}>
            <AddToAppContextProvider>
              <SnackbarProvider
                Components={{
                  loading: ResponseMessage,
                  error: ResponseMessage,
                  success: ResponseMessage,
                  info: ResponseMessage,
                  warning: ResponseMessage
                }}
                maxSnack={5}
              >
                <GlobalLoaderProvider>
                  <SnackbarNotificationsProvider>
                    <CssBaseline />
                    <GlobalStyles
                      styles={{
                        '.notistack-SnackbarContainer': { zIndex: `${Z_INDEX.snackbar} !important` }
                      }}
                    />
                    <RouterProvider router={router} />
                  </SnackbarNotificationsProvider>
                </GlobalLoaderProvider>
              </SnackbarProvider>
            </AddToAppContextProvider>
          </ReduxStoreProvider>
        </ThemeProvider>
        <ReactQueryDevtools initialIsOpen={false} />
      </QueryClientProvider>
    </CookiesProvider>
  );
};

export default App;
